import { useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SyncAltOutlined } from "@mui/icons-material";
import type { QuarterVolume } from "../../../../../store/types/secureVolume.types";
import type { InputMode } from "../types";
import { fmt, pctFmt } from "../types";
import {
  getInputValue,
  getQuarterForecast,
  kwhFromPercent,
  percentFromKwh,
} from "../helpers";

const compactFieldSx = {
  "& .MuiInputBase-root": { height: 28 },
  "& .MuiInputBase-input": {
    py: 0.25,
    fontSize: "0.8rem",
    textAlign: "right",
    /* Hide number spinners */
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    MozAppearance: "textfield",
  },
} as const;

interface InputQuarterCellProps {
  keyPrefix: string;
  year: number;
  quarter: number;
  volumes: QuarterVolume[];
  inputValues: Record<string, string>;
  onInputChange: (updates: Record<string, string>) => void;
}

export function InputQuarterCell({
  keyPrefix,
  year,
  quarter,
  volumes,
  inputValues,
  onInputChange,
}: InputQuarterCellProps) {
  const forecast = getQuarterForecast(volumes, year, quarter);
  const hasForecast = forecast > 0;

  const [mode, setMode] = useState<InputMode>("number");

  const kwhKey = `${keyPrefix}-${year}-${quarter}`;
  const pctKey = `${kwhKey}-pct`;

  const kwhVal = getInputValue(keyPrefix, year, quarter, volumes, inputValues);
  const numKwh = Number(kwhVal) || 0;
  const derivedPct = percentFromKwh(numKwh, forecast);

  // For % mode: use stored pct string if non-empty, otherwise derive from kWh
  const storedPct = inputValues[pctKey];
  const pctInputVal = storedPct
    ? storedPct
    : numKwh > 0
      ? derivedPct.toFixed(1)
      : "";

  const handleKwhChange = (value: string) => {
    const updates: Record<string, string> = { [kwhKey]: value };
    if (pctKey in inputValues) {
      updates[pctKey] = "";
    }
    onInputChange(updates);
  };

  const handlePctChange = (value: string) => {
    const pctNum = Number(value) || 0;
    const computedKwh = kwhFromPercent(pctNum, forecast);
    onInputChange({
      [kwhKey]: computedKwh.toString(),
      [pctKey]: value,
    });
  };

  const toggleMode = () => {
    const newMode = mode === "percent" ? "number" : "percent";
    setMode(newMode);
    if (pctKey in inputValues) {
      onInputChange({ [pctKey]: "" });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        justifyContent: "flex-end",
        mt: 0.5,
      }}
    >
      {mode === "number" ? (
        <Box sx={{ textAlign: "right" }}>
          <TextField
            size="small"
            type="number"
            placeholder="0"
            value={kwhVal}
            onChange={(e) => handleKwhChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            sx={{ width: 110, ...compactFieldSx }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                    >
                      kWh
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
          />
          {hasForecast && numKwh > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {pctFmt.format(derivedPct)}% av prognos
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: "right" }}>
          <TextField
            size="small"
            type="number"
            placeholder="0"
            value={pctInputVal}
            onChange={(e) => handlePctChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            sx={{ width: 84, ...compactFieldSx }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                    >
                      %
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
          />
          {numKwh > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {fmt.format(numKwh)} kWh
            </Typography>
          )}
        </Box>
      )}

      {hasForecast && (
        <Tooltip
          title={mode === "percent" ? "Växla till kWh" : "Växla till %"}
        >
          <IconButton size="small" onClick={toggleMode} sx={{ p: 0.25 }}>
            <SyncAltOutlined sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
